// Teacher → Assignments. Assign a published lesson to one of the teacher's
// classes (optional due date), list existing assignments, unassign. R9.

import { useT } from '@fajrarabi/i18n/react';
import { Alert, Button, Card, CardBody, Field, Input, Skeleton } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { ApiError } from '~/lib/api';
import {
  createAssignment,
  deleteAssignment,
  listAssignableLessons,
  listTeacherAssignments,
  listTeacherClasses,
  type Assignment,
} from '~/lib/school';

export const Route = createFileRoute('/$locale/teacher/assignments')({
  component: AssignmentsScreen,
});

function AssignmentsScreen() {
  const t = useT();
  const qc = useQueryClient();
  const classes = useQuery({
    queryKey: ['teacher', 'classes'],
    queryFn: ({ signal }) => listTeacherClasses(signal),
  });
  const lessons = useQuery({
    queryKey: ['teacher', 'lessons'],
    queryFn: ({ signal }) => listAssignableLessons(signal),
  });
  const assignments = useQuery({
    queryKey: ['teacher', 'assignments'],
    queryFn: ({ signal }) => listTeacherAssignments(signal),
  });

  const [classId, setClassId] = useState('');
  const [lessonId, setLessonId] = useState('');
  const [dueAt, setDueAt] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createAssignment({
        classId,
        lessonId,
        dueAt: dueAt ? new Date(dueAt).toISOString() : null,
      }),
    onSuccess: async () => {
      setDueAt('');
      await qc.invalidateQueries({ queryKey: ['teacher', 'assignments'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteAssignment(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['teacher', 'assignments'] });
    },
  });

  const canSubmit = classId !== '' && lessonId !== '';

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-bold text-ink-900">{t('app.teacher.assignments_title')}</h1>

      <Card className="mt-6">
        <CardBody>
          {create.isError && (
            <Alert tone="danger" className="mb-4">
              {create.error instanceof ApiError
                ? create.error.message
                : t('app.common.error_generic')}
            </Alert>
          )}
          <form
            className="grid gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) create.mutate();
            }}
          >
            <Field label={t('app.teacher.pick_class')}>
              {(id) => (
                <select
                  id={id}
                  value={classId}
                  onChange={(e) => setClassId(e.target.value)}
                  required
                  className="h-11 w-full rounded-xl border border-ink-500/20 bg-white px-3 text-ink-900"
                >
                  <option value="">—</option>
                  {(classes.data ?? []).map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label={t('app.teacher.pick_lesson')}>
              {(id) => (
                <select
                  id={id}
                  value={lessonId}
                  onChange={(e) => setLessonId(e.target.value)}
                  required
                  className="h-11 w-full rounded-xl border border-ink-500/20 bg-white px-3 text-ink-900"
                >
                  <option value="">—</option>
                  {(lessons.data ?? []).map((l) => (
                    <option key={l.id} value={l.id}>
                      {t(l.titleKey)}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label={t('app.teacher.due_date')}>
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                />
              )}
            </Field>
            <div className="flex items-end">
              <Button type="submit" disabled={!canSubmit || create.isPending}>
                {t('app.teacher.assign')}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <div className="mt-8 space-y-3">
        {assignments.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (assignments.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-ink-700">{t('app.teacher.no_assignments')}</p>
        ) : (
          assignments.data!.map((a) => (
            <AssignmentRow key={a.id} assignment={a} onRemove={() => remove.mutate(a.id)} />
          ))
        )}
      </div>
    </div>
  );
}

function AssignmentRow({ assignment, onRemove }: { assignment: Assignment; onRemove: () => void }) {
  const t = useT();
  return (
    <div className="flex items-center justify-between rounded-2xl bg-white p-4 shadow-[0_12px_30px_-22px_rgba(31,26,23,0.25)] ring-1 ring-burgundy-500/10">
      <div>
        <p className="font-semibold text-ink-900">{t(assignment.lessonTitleKey)}</p>
        <p className="mt-0.5 text-sm text-ink-500">
          {assignment.className}
          {assignment.dueAt ? ` · ${assignment.dueAt.slice(0, 10)}` : ''}
        </p>
      </div>
      <Button variant="ghost" size="sm" onClick={onRemove}>
        {t('app.teacher.unassign')}
      </Button>
    </div>
  );
}
